import React, { useState, useEffect } from 'react';
import { Loader, AlertCircle, Search, MapPin, Calendar, Package, Link2, Eye, X, Trash2, RefreshCw, ArrowRight } from 'lucide-react';
import RequestLinkDialog from './RequestLinkDialog';

const OpenRequestsViewer = ({ onBack }) => {
  const [requests, setRequests] = useState([]);
  const [updatedDeals, setUpdatedDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [deletingRequestId, setDeletingRequestId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('requests'); // 'requests' or 'updated'

  // Helper function to create correct API URL
  const createApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
    const cleanEndpoint = endpoint.replace(/^\/+/, '');
    
    if (cleanBaseUrl.endsWith('/api')) {
      const endpointWithoutApi = cleanEndpoint.replace(/^api\//, '');
      return `${cleanBaseUrl}/${endpointWithoutApi}`;
    }
    
    return `${cleanBaseUrl}/${cleanEndpoint}`;
  };

  // Load pending requests and updated deals
  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Nicht authentifiziert');
          setLoading(false);
          return;
        }

        // Load pending requests
        const requestsUrl = createApiUrl('api/requests/pending');
        const requestsResponse = await fetch(requestsUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!requestsResponse.ok) {
          throw new Error('Fehler beim Laden der Anfragen');
        }

        const requestsData = await requestsResponse.json();
        setRequests(requestsData || []);

        // Load updated deals
        const dealsUrl = createApiUrl('api/deals/updated');
        const dealsResponse = await fetch(dealsUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (dealsResponse.ok) {
          const dealsData = await dealsResponse.json();
          setUpdatedDeals(dealsData || []);
        } else {
          console.warn('Could not load updated deals:', dealsResponse.status);
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Fehler beim Laden der Daten');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Load request details
  const loadRequestDetails = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      const url = createApiUrl(`api/requests/${requestId}`);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Details');
      }

      const data = await response.json();
      setSelectedRequest(data);
      setShowDetails(true);
    } catch (err) {
      console.error('Error loading request details:', err);
      alert('Fehler beim Laden der Details');
    }
  };

  // Handle successful linking
  const handleLinkSuccess = (linkData) => {
    // Remove linked request from list
    setRequests(prev => prev.filter(r => r.id !== selectedRequest?.id));
    setShowLinkDialog(false);
    setShowDetails(false);
    setSelectedRequest(null);
    
    // Navigate to inspection step 2 with deal_id and inspection_id
    if (linkData && linkData.deal_id && linkData.inspection_id) {
      // Store inspection data for navigation
      sessionStorage.setItem('linkedInspection', JSON.stringify({
        deal_id: linkData.deal_id,
        inspection_id: linkData.inspection_id
      }));
      
      // Trigger navigation by calling parent callback if available
      if (onBack) {
        // We'll handle navigation in App.js
        window.location.href = `/?deal_id=${linkData.deal_id}&inspection_id=${linkData.inspection_id}&step=2`;
      }
    }
  };

  // Handle delete request
  const handleDeleteRequest = async (requestId) => {
    setRequestToDelete(requestId);
    setShowDeleteConfirm(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!requestToDelete) return;

    setDeletingRequestId(requestToDelete);
    try {
      const token = localStorage.getItem('token');
      const url = createApiUrl(`api/requests/${requestToDelete}`);
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Fehler beim Löschen der Anfrage');
      }

      // Remove deleted request from list
      setRequests(prev => prev.filter(r => r.id !== requestToDelete));
      setShowDeleteConfirm(false);
      setRequestToDelete(null);
    } catch (err) {
      console.error('Error deleting request:', err);
      alert('Fehler beim Löschen der Anfrage');
    } finally {
      setDeletingRequestId(null);
    }
  };

  // Filter requests based on search
  const filteredRequests = requests.filter(request => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      request.customer_name?.toLowerCase().includes(query) ||
      request.customer_email?.toLowerCase().includes(query) ||
      request.origin_address?.toLowerCase().includes(query) ||
      request.destination_address?.toLowerCase().includes(query)
    );
  });

  // Filter updated deals based on search
  const filteredUpdatedDeals = updatedDeals.filter(deal => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      deal.customer_name?.toLowerCase().includes(query) ||
      deal.customer_email?.toLowerCase().includes(query) ||
      deal.origin_address?.toLowerCase().includes(query) ||
      deal.destination_address?.toLowerCase().includes(query)
    );
  });

  // Handle navigation to deal
  const handleNavigateToDeal = (dealId, inspectionId) => {
    // Navigate to deal with inspection step 2 (room items selector)
    window.location.href = `/?deal_id=${dealId}&inspection_id=${inspectionId}&step=2`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Nicht angegeben';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Lade offene Anfragen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Fehler</h2>
          <p className="text-gray-600">{error}</p>
          {onBack && (
            <button
              onClick={onBack}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Zurück
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Anfragen & Aktualisierungen</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Offene Anfragen und aktualisierte Deals
              </p>
            </div>
            {onBack && (
              <button
                onClick={onBack}
                className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Zurück
              </button>
            )}
          </div>

          {/* Stats */}
          <div className="mt-4 sm:mt-6 flex items-center gap-4 flex-wrap">
            <div className="bg-blue-50 rounded-lg p-3 sm:p-4 border border-blue-100">
              <div className="text-xs sm:text-sm text-gray-600 mb-1">Offene Anfragen</div>
              <div className="text-xl sm:text-2xl font-bold text-gray-900">
                {requests.length}
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 sm:p-4 border border-green-100">
              <div className="text-xs sm:text-sm text-gray-600 mb-1">Aktualisierte Deals</div>
              <div className="text-xl sm:text-2xl font-bold text-gray-900">
                {updatedDeals.length}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm p-2 mb-4 sm:mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'requests'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Offene Anfragen ({requests.length})
            </button>
            <button
              onClick={() => setActiveTab('updated')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'updated'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Aktualisierte Deals ({updatedDeals.length})
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {((activeTab === 'requests' && requests.length > 0) || (activeTab === 'updated' && updatedDeals.length > 0)) && (
          <div className="mb-4 sm:mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nach Name, E-Mail oder Adresse suchen..."
                className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Updated Deals List */}
        {activeTab === 'updated' && (
          <>
            {filteredUpdatedDeals.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {filteredUpdatedDeals.map((deal) => (
                  <div
                    key={deal.deal_id}
                    className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {deal.customer_name || 'Unbekannt'}
                        </h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          {deal.customer_email && (
                            <div className="flex items-center gap-2">
                              <span>📧</span>
                              <span>{deal.customer_email}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                        <RefreshCw className="h-3 w-3 inline mr-1" />
                        Aktualisiert
                      </div>
                    </div>

                    <div className="space-y-3 mb-4">
                      <div className="flex items-start gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-500">Von</div>
                          <div className="text-gray-900 truncate">{deal.origin_address || 'Nicht angegeben'}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-500">Nach</div>
                          <div className="text-gray-900 truncate">{deal.destination_address || 'Nicht angegeben'}</div>
                        </div>
                      </div>
                      {deal.move_date && (
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600">Umzugsdatum: {formatDate(deal.move_date)}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm">
                        <Package className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">
                          {deal.total_rooms || 0} Räume • {deal.total_volume?.toFixed(2) || '0.00'} m³
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Aktualisiert: {formatDate(deal.updated_at)}
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => handleNavigateToDeal(deal.deal_id, deal.inspection_id)}
                        className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <ArrowRight className="h-4 w-4" />
                        Zum Deal
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : updatedDeals.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center">
                <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Keine aktualisierten Deals</h3>
                <p className="text-gray-600">Es gibt derzeit keine Deals mit Kundenaktualisierungen.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Keine Deals gefunden, die "{searchQuery}" enthalten.</p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 text-blue-600 hover:text-blue-700 text-sm"
                >
                  Suche zurücksetzen
                </button>
              </div>
            )}
          </>
        )}

        {/* Requests List */}
        {activeTab === 'requests' && filteredRequests.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {filteredRequests.map((request) => (
              <div
                key={request.id}
                className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {request.customer_name || 'Unbekannt'}
                    </h3>
                    <div className="space-y-1 text-sm text-gray-600">
                      {request.customer_email && (
                        <div className="flex items-center gap-2">
                          <span>📧</span>
                          <span>{request.customer_email}</span>
                        </div>
                      )}
                      {request.customer_phone && (
                        <div className="flex items-center gap-2">
                          <span>📞</span>
                          <span>{request.customer_phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium">
                    Offen
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-500">Von</div>
                      <div className="text-gray-900 truncate">{request.origin_address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-500">Nach</div>
                      <div className="text-gray-900 truncate">{request.destination_address}</div>
                    </div>
                  </div>
                  {request.move_date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">Umzugsdatum: {formatDate(request.move_date)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <Package className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">
                      {request.total_rooms || 0} Räume • {request.total_volume?.toFixed(2) || '0.00'} m³
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Erstellt: {formatDate(request.created_at)}
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => loadRequestDetails(request.id)}
                    className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    Details
                  </button>
                  <button
                    onClick={() => {
                      loadRequestDetails(request.id);
                      setShowLinkDialog(true);
                    }}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <Link2 className="h-4 w-4" />
                    Verknüpfen
                  </button>
                  <button
                    onClick={() => handleDeleteRequest(request.id)}
                    disabled={deletingRequestId === request.id}
                    className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Anfrage löschen"
                  >
                    {deletingRequestId === request.id ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : activeTab === 'requests' && requests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Keine offenen Anfragen</h3>
            <p className="text-gray-600">Es gibt derzeit keine offenen Kundenanfragen.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Keine Anfragen gefunden, die "{searchQuery}" enthalten.</p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 text-blue-600 hover:text-blue-700 text-sm"
            >
              Suche zurücksetzen
            </button>
          </div>
        )}

        {/* Request Details Modal */}
        {showDetails && selectedRequest && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4 animate-fadeIn"
            onClick={() => setShowDetails(false)}
          >
            <div 
              className="bg-white rounded-none sm:rounded-xl shadow-2xl max-w-4xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col animate-slideUp"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Anfrage-Details</h2>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedRequest(null);
                  }}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                <div className="space-y-6">
                  {/* Customer Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Kundendaten</h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div><span className="font-medium">Name:</span> {selectedRequest.customer_name}</div>
                      <div><span className="font-medium">E-Mail:</span> {selectedRequest.customer_email}</div>
                      {selectedRequest.customer_phone && (
                        <div><span className="font-medium">Telefon:</span> {selectedRequest.customer_phone}</div>
                      )}
                    </div>
                  </div>

                  {/* Addresses */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Adressen</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">Abholadresse</div>
                        <div className="font-medium">{selectedRequest.origin_address}</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">Lieferadresse</div>
                        <div className="font-medium">{selectedRequest.destination_address}</div>
                      </div>
                    </div>
                  </div>

                  {/* Move Info */}
                  {selectedRequest.move_date && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Umzugsdatum</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        {formatDate(selectedRequest.move_date)}
                      </div>
                    </div>
                  )}

                  {/* Statistics */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Statistiken</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">Räume</div>
                        <div className="text-xl font-bold">{selectedRequest.total_rooms || 0}</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">Volumen</div>
                        <div className="text-xl font-bold">{selectedRequest.total_volume?.toFixed(2) || '0.00'} m³</div>
                      </div>
                    </div>
                  </div>

                  {/* Timestamps */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Zeitstempel</h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                      <div><span className="font-medium">Erstellt:</span> {formatDate(selectedRequest.created_at)}</div>
                      {selectedRequest.updated_at && (
                        <div><span className="font-medium">Zuletzt aktualisiert:</span> {formatDate(selectedRequest.updated_at)}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 sm:p-6 border-t border-gray-200 flex gap-3">
                <button
                  onClick={() => setShowDetails(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Schließen
                </button>
                <button
                  onClick={() => {
                    if (selectedRequest) {
                      handleDeleteRequest(selectedRequest.id);
                      setShowDetails(false);
                    }
                  }}
                  disabled={deletingRequestId === selectedRequest?.id}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {deletingRequestId === selectedRequest?.id ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      Wird gelöscht...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Löschen
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowLinkDialog(true);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Link2 className="h-4 w-4" />
                  Mit Deal verknüpfen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Link Dialog */}
        {showLinkDialog && selectedRequest && (
          <RequestLinkDialog
            request={selectedRequest}
            onClose={() => {
              setShowLinkDialog(false);
              setShowDetails(false);
              setSelectedRequest(null);
            }}
            onSuccess={handleLinkSuccess}
          />
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn"
            onClick={() => {
              if (!deletingRequestId) {
                setShowDeleteConfirm(false);
                setRequestToDelete(null);
              }
            }}
          >
            <div 
              className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-slideUp"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-red-100 rounded-full p-3">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Anfrage löschen?</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Möchten Sie diese Anfrage wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setRequestToDelete(null);
                    }}
                    disabled={deletingRequestId !== null}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={confirmDelete}
                    disabled={deletingRequestId !== null}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {deletingRequestId !== null ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        Wird gelöscht...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4" />
                        Löschen
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpenRequestsViewer;

